<!--
 * @Author: wjc
 * @Date: 2025-09-10 16:44:08
 * @LastEditors: wjc
 * @LastEditTime: 2026-05-11 16:34:51
 * @Description: 
-->
<template>
  <Primitive
    v-if="!showTooltip"
    v-bind="forwardedWithType"
    :as="as"
    :as-child="asChild"
    :class="cn(buttonVariants({ variant, size, rounded }), props.class)"
    :disabled="isDisabled"
  >
    <i
      v-if="loading"
      class="i-lucide-loader-circle text-md mr-2 size-4 flex-shrink-0 animate-spin"
    ></i>
    <slot></slot>
  </Primitive>
  <NiceTooltip v-else :delay-duration="tooltipDelayDuration" :side="tooltipSide">
    <template #trigger>
      <Primitive
        v-bind="forwardedWithType"
        :as="as"
        :as-child="asChild"
        :class="cn(buttonVariants({ variant, size }), props.class)"
        :disabled="isDisabled"
      >
        <i
          v-if="loading"
          class="i-lucide-loader-circle text-md mr-2 size-4 flex-shrink-0 animate-spin"
        ></i>
        <slot></slot>
      </Primitive>
    </template>
    <slot v-if="slots.tooltip" name="tooltip"></slot>
    <template v-else>
      {{ tooltip }}
    </template>
  </NiceTooltip>
</template>

<script setup lang="ts">
  import type { NiceButtonProps } from './types'

  import { computed, toRaw, useSlots } from 'vue'

  import { cn } from '@nice/utils'
  import { cva } from 'class-variance-authority'
  import { Primitive, useForwardPropsEmits } from 'reka-ui'

  import { NiceTooltip } from '../tooltip'

  interface Props extends NiceButtonProps {}
  defineOptions({
    name: 'NiceButton',
  })
  const props = withDefaults(defineProps<Props>(), {
    as: 'button',
    class: '',
    disabled: false,
    loading: false,
    size: 'default',
    variant: 'default',
    rounded: 'default',
    tooltipDelayDuration: 200,
    tooltipSide: 'bottom',
  })
  const emit = defineEmits<{
    (e: 'click', ev: Event): void
  }>()
  const forwarded = useForwardPropsEmits(props)
  const forwardedWithType = computed(() => {
    // 使用 toRaw 保留 listeners / 原始引用，避免展开 proxy 丢失事件处理
    const raw = toRaw(forwarded as any) || (forwarded as any)
    const base = { ...(raw as any) }
    if (props.as === 'button' && (base.type === undefined || base.type === null)) {
      base.type = 'button'
    }
    // 如果没有外部传入 onClick/onclick，内部绑定一个会转发到组件的 emit，
    // 保证模板上 @click 生效（例如 <NiceButton @click="...">）
    if (!base.onClick && !base.onclick) {
      base.onClick = (ev: Event) => {
        emit('click', ev)
      }
    }
    return base
  })
  const buttonVariants = cva(
    'inline-flex cursor-pointer items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed  disabled:opacity-50',
    {
      defaultVariants: {
        size: 'default',
        variant: 'default',
        rounded: 'default',
      },
      variants: {
        size: {
          default: 'h-8 px-4 py-1',
          icon: 'h-8 w-8 rounded-full px-1 text-lg',
          lg: 'h-10 px-4',
          sm: 'h-8 px-2 text-xs',
          xs: 'h-8 w-8 px-1 text-xs',
        },
        variant: {
          default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
          destructive:
            'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive-hover',
          ghost: 'hover:bg-accent hover:text-accent-foreground border b-border text-foreground/80',
          heavy: 'hover:bg-heavy hover:text-heavy-foreground',
          icon: 'hover:bg-accent hover:text-accent-foreground text-foreground/80',
          link: 'text-primary underline-offset-4 hover:underline',
          outline:
            'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
          secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        },
        rounded: {
          default: 'rounded-md',
          full: 'rounded-full',
          lg: 'rounded-2xl',
          md: 'rounded-md',
          sm: 'rounded-sm',
          xs: 'rounded-xs',
        },
      },
    }
  )

  const slots = useSlots()
  const showTooltip = computed(() => !!slots.tooltip || !!props.tooltip)

  const isDisabled = computed(() => {
    return props.disabled || props.loading
  })
</script>
